import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Typography } from '@mui/material';
import './EvalSim.css';
import '../text-chat/TextChat.css';
import { EvalChatBubble, EvalMessage as Message } from '../../components/Chat/EvalChatBubble';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';
import { useChat } from '../../lib/hooks/UseChat';
import { usePersona } from '../../lib/hooks/UsePersona';
import { Persona } from '../../lib/api/Persona';
import { ConversationPersona, ConversationObjective, SimulatedUserTurn, ConversationMessage } from '@common/evals/conversation/types';
import { API_URL } from '../../lib/constants';
import { DownloadIcon, ChatIcon } from '../../components/Chat/ChatIcons';
import { consumeChatStream } from '../../lib/helpers/chatStream';

// ================================================================
// Eval Simulator Page
// ================================================================
const EvalSimPage: React.FC = () => {
    const { userInfo } = useUserInfoContext();
    const { getPersonas } = usePersona();
    const { postMessageStream } = useChat();

    // Configuration state
    const [assistantPersonas, setAssistantPersonas] = useState<Persona[]>([]);
    const [evalPersonas, setEvalPersonas] = useState<ConversationPersona[]>([]);
    const [objectives, setObjectives] = useState<ConversationObjective[]>([]);

    const [selectedAssistantId, setSelectedAssistantId] = useState<string>('');
    const [selectedEvalPersonaId, setSelectedEvalPersonaId] = useState<string>('');
    const [selectedObjectiveId, setSelectedObjectiveId] = useState<string>('');

    // UI State
    const [messages, setMessages] = useState<Message[]>([]);
    const [isEvalRunning, setIsEvalRunning] = useState(false);
    const [autoContinue, setAutoContinue] = useState(true);
    const [status, setStatus] = useState<'idle' | 'running' | 'stopped'>('idle');
    const [turnNumber, setTurnNumber] = useState(1);
    const maxTurns = 6;

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesRef = useRef<Message[]>([]);
    const isEvalRunningRef = useRef(false);
    const autoContinueRef = useRef(true);

    // Sync refs with state so callbacks always have latest values
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    useEffect(() => {
        isEvalRunningRef.current = isEvalRunning;
    }, [isEvalRunning]);

    useEffect(() => {
        autoContinueRef.current = autoContinue;
    }, [autoContinue]);

    // Derived state
    const selectedAssistant = useMemo(() =>
        assistantPersonas.find(p => p.id === selectedAssistantId),
    [assistantPersonas, selectedAssistantId]);

    const selectedEvalPersona = useMemo(() =>
        evalPersonas.find(p => p.id === selectedEvalPersonaId),
    [evalPersonas, selectedEvalPersonaId]);

    const selectedObjective = useMemo(() =>
        objectives.find(p => p.id === selectedObjectiveId),
    [objectives, selectedObjectiveId]);

    useEffect(() => {
        if (userInfo) {
            getPersonas()
                .then(setAssistantPersonas)
                .catch(err => console.error("Failed to load personas:", err));

            fetch(`${API_URL}/test/conversation-evals/config`, {
                headers: {
                    'Authorization': `Bearer ${userInfo.accessToken}`
                }
            })
            .then(res => res.json())
            .then(data => {
                setEvalPersonas(data.personas);
                setObjectives(data.objectives);
                if (data.personas.length > 0) setSelectedEvalPersonaId(data.personas[0].id);
                if (data.objectives.length > 0) setSelectedObjectiveId(data.objectives[0].id);
            });
        }
    }, [userInfo, getPersonas]);

    // Set default assistant
    useEffect(() => {
        if (assistantPersonas.length > 0 && !selectedAssistantId) {
            const chip = assistantPersonas.find(p => p.id === 'p072690a2e4b') || assistantPersonas[0];
            setSelectedAssistantId(chip.id || '');
        }
    }, [assistantPersonas, selectedAssistantId]);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Helper for streaming text
    const streamText = useCallback(async (text: string, role: 'user' | 'assistant', rationale?: string, turn?: number) => {
        const id = `${role}-${Date.now()}`;
        setMessages(prev => [...prev, { id, role, content: '', isStreaming: true, rationale, turnNumber: turn }]);

        const chunks = text.split(' ');

        for (let i = 0; i < chunks.length; i++) {
            const currentChunkIndex = i;
            setMessages(prev => prev.map(m => {
                if (m.id === id) {
                    const accumulatedUntilNow = chunks.slice(0, currentChunkIndex + 1).join(' ');
                    return { ...m, content: accumulatedUntilNow };
                }
                return m;
            }));
            // Simulate typing speed
            await new Promise(resolve => setTimeout(resolve, 30 + Math.random() * 40));
        }

        setMessages(prev => prev.map(m => m.id === id ? { ...m, isStreaming: false } : m));
        return { id, content: text };
    }, []);

    // Main turn logic
    const runNextTurn = useCallback(async (currentTurn: number) => {
        if (!userInfo || !selectedEvalPersonaId || !selectedObjectiveId || !selectedAssistant) return;
        if (!isEvalRunningRef.current && currentTurn > 1) return;

        setStatus('running');
        setTurnNumber(currentTurn);

        let activeAssistantId: string | null = null;
        try {
            // 1. Get next turn from Simulator
            const transcript: ConversationMessage[] = messagesRef.current.map(m => ({
                role: m.role,
                content: m.content
            }));

            const simResponse = await fetch(`${API_URL}/test/conversation-evals/next-turn`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${userInfo.accessToken}`
                },
                body: JSON.stringify({
                    personaId: selectedEvalPersonaId,
                    objectiveId: selectedObjectiveId,
                    transcript,
                    turnNumber: currentTurn,
                    maxTurns
                })
            });

            if (!simResponse.ok) throw new Error('Failed to get simulator turn');
            const turnData: SimulatedUserTurn = await simResponse.json();

            if (!isEvalRunningRef.current) {
                setStatus('idle');
                return;
            }

            // 2. Stream user message
            await streamText(turnData.nextUserMessage, 'user', turnData.rationale, currentTurn);

            if (!isEvalRunningRef.current) {
                setStatus('idle');
                return;
            }

            const updatedTranscript = [
                ...transcript,
                { role: 'user' as const, content: turnData.nextUserMessage }
            ];

            // 3. Get assistant response
            const assistantId = `assistant-${Date.now()}`;
            activeAssistantId = assistantId;
            const clientTurnId = crypto.randomUUID();
            setMessages(prev => [...prev, { id: assistantId, role: 'assistant', content: '', isStreaming: true }]);

            const response = await postMessageStream(
                turnData.nextUserMessage,
                userInfo,
                'undefined', // Use 'undefined' to avoid saving to backend history by default
                selectedAssistant,
                updatedTranscript.slice(0, -1), // useChat takes history *before* the current message
                undefined,
                { clientTurnId }
            );

            if (!response?.ok) throw new Error('Assistant API error');

            await consumeChatStream(response, (update) => {
                if (!isEvalRunningRef.current) return;
                setMessages(prev => prev.map(m => {
                    if (m.id === assistantId) {
                        return {
                            ...m,
                            content: update.annotatedAnswer || update.accumulatedContent,
                            sources: update.sources || m.sources,
                            seeds: update.seeds || m.seeds,
                            retrievedChunkIds: update.retrievedChunkIds || m.retrievedChunkIds,
                            usedChunkIds: update.usedChunkIds || m.usedChunkIds,
                            isStreaming: !(update.annotatedAnswer || update.sources)
                        };
                    }

                    // Keep safety route on the user turn
                    if (m.role === 'user' && m === prev[prev.length - 2]) {
                        return {
                            ...m,
                            safetyRoute: update.safetyRoute || m.safetyRoute,
                        };
                    }

                    return m;
                }));
            });

            setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, isStreaming: false } : m));

            if (!isEvalRunningRef.current) {
                setStatus('idle');
                return;
            }

            // 4. Decide whether to continue
            const isFinished = turnData.isFinalTurn || currentTurn >= maxTurns;

            if (!isFinished && autoContinueRef.current) {
                setTurnNumber(currentTurn + 1);
                setTimeout(() => runNextTurn(currentTurn + 1), 1500);
            } else {
                setStatus('idle');
                setIsEvalRunning(false);
            }

        } catch (error) {
            console.error('Eval error:', error);
            // A stream that fails after the bubble was added would otherwise keep
            // its streaming cursor forever.
            const failedId = activeAssistantId;
            if (failedId) {
                setMessages(prev => prev.map(m => m.id === failedId ? { ...m, isStreaming: false } : m));
            }
            setStatus('stopped');
            setIsEvalRunning(false);
        }
    }, [userInfo, selectedEvalPersonaId, selectedObjectiveId, selectedAssistant, postMessageStream, streamText]);

    const handleStart = () => {
        setMessages([]);
        setTurnNumber(1);
        setIsEvalRunning(true);
        // We use a timeout to let state update if needed, though not strictly necessary here
        setTimeout(() => runNextTurn(1), 0);
    };

    const handleStop = () => {
        setIsEvalRunning(false);
        setStatus('stopped');
    };

    const handleContinue = () => {
        if (status === 'running') return;
        setIsEvalRunning(true);
        runNextTurn(turnNumber);
    };

    const handleDownload = () => {
        const header = `EVALUATION TRANSCRIPT\n` +
                       `=====================\n` +
                       `Date: ${new Date().toLocaleString()}\n` +
                       `Assistant: ${selectedAssistant?.name} (${selectedAssistantId})\n` +
                       `Eval Persona: ${selectedEvalPersona?.name} (${selectedEvalPersonaId})\n` +
                       `Objective: ${selectedObjective?.name} (${selectedObjectiveId})\n` +
                       `=====================\n\n`;

        const transcript = messages.map(m => {
            const role = m.role.toUpperCase();
            const turn = m.turnNumber ? `[Turn ${m.turnNumber}] ` : '';
            let text = `${turn}${role}: ${m.content}`;
            if (m.rationale) {
                text += `\n   Rationale: ${m.rationale}`;
            }
            return text;
        }).join('\n\n');

        const blob = new Blob([header + transcript], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `eval-transcript-${selectedEvalPersonaId}-${Date.now()}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="eval-chat-page">
            <aside className="eval-sidebar">
                <h2>Simulator Setup</h2>

                <div className="eval-config-section">
                    <h3>Target Assistant</h3>
                    <div className="eval-selector-wrapper">
                        <select
                            className="eval-selector"
                            value={selectedAssistantId}
                            onChange={(e) => setSelectedAssistantId(e.target.value)}
                        >
                            {[...assistantPersonas].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="eval-config-section">
                    <h3>Eval Persona</h3>
                    <div className="eval-selector-wrapper">
                        <select
                            className="eval-selector"
                            value={selectedEvalPersonaId}
                            onChange={(e) => setSelectedEvalPersonaId(e.target.value)}
                        >
                            {evalPersonas.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>
                    {selectedEvalPersona && (
                        <div className="agent-details">
                            <p>{selectedEvalPersona.description}</p>
                            <h3>Simulator Prompt</h3>
                            <div className="prompt-display">
                                {selectedEvalPersona.simulatorPrompt}
                            </div>
                        </div>
                    )}
                </div>

                <div className="eval-config-section">
                    <h3>Objective</h3>
                    <div className="eval-selector-wrapper">
                        <select
                            className="eval-selector"
                            value={selectedObjectiveId}
                            onChange={(e) => setSelectedObjectiveId(e.target.value)}
                        >
                            {objectives.map(o => (
                                <option key={o.id} value={o.id}>{o.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="eval-controls">
                    <label className="auto-continue-toggle">
                        <input
                            type="checkbox"
                            checked={autoContinue}
                            onChange={(e) => setAutoContinue(e.target.checked)}
                        />
                        Auto-continue conversation
                    </label>

                    {!isEvalRunning ? (
                        <button className="start-eval-button" onClick={handleStart}>
                            Start New Conversation
                        </button>
                    ) : (
                        <button className="stop-eval-button" onClick={handleStop}>
                            Stop Conversation
                        </button>
                    )}

                    {messages.length > 0 && !isEvalRunning && status !== 'running' && turnNumber < maxTurns && (
                        <button className="start-eval-button" onClick={handleContinue}>
                            Continue Conversation
                        </button>
                    ) }

                    {messages.length > 0 && (
                        <button className="download-transcript-button" onClick={handleDownload}>
                            <DownloadIcon /> Download Transcript
                        </button>
                    )}
                </div>
            </aside>

            <main className="eval-chat-container">
                <header className="eval-chat-header">
                    <Typography variant="h5" component="h1">
                        Simulator: {selectedEvalPersona?.name || 'Agent'} vs {selectedAssistant?.name || 'Assistant'}
                    </Typography>
                    <div className={`eval-status-badge ${status}`}>
                        {status}
                    </div>
                </header>

                <div className="eval-messages-container">
                    {messages.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-icon">
                                <ChatIcon />
                            </div>
                            <h2>Ready for Evaluation</h2>
                            <p>Select an agent and objective to begin the automated conversation</p>
                        </div>
                    ) : (
                        <>
                            {messages.map((message, index) => (
                                <React.Fragment key={message.id}>
                                    {message.role === 'user' && message.turnNumber && (
                                        <div className="turn-counter">Turn {message.turnNumber}</div>
                                    )}
                                    <EvalChatBubble
                                        message={message}
                                        persona={selectedAssistant}
                                        evalPersonaName={selectedEvalPersona?.name}
                                    />
                                </React.Fragment>
                            ))}
                            {status === 'running' && (
                                (messages[messages.length-1]?.role === 'assistant' && !messages[messages.length-1]?.isStreaming) ||
                                (messages.length === 0)
                            ) && (
                                <div className="typing-indicator">
                                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginRight: 'var(--space-sm)' }}>
                                        {selectedEvalPersona?.name} is thinking...
                                    </span>
                                    <div className="typing-dot" />
                                    <div className="typing-dot" />
                                    <div className="typing-dot" />
                                </div>
                            )}
                        </>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            </main>
        </div>
    );
};

export default EvalSimPage;
